/**
 * Datenform des Anlagenbuchs — gemeinsam für Demo und Live.
 * Live speist sie sich aus GET /api/anlagen + GET /api/pflichten/faellig + GET /api/objekte,
 * im Demo-Build aus den Beispieldaten. Die Seite rendert in beiden Fällen identisch.
 */

import { DEMO_ANLAGEN, DEMO_PFLICHTEN } from './demodaten';
import type { Ampel } from './startseite-daten';

export interface PflichtAnsicht {
  id: string;
  bezeichnung: string;
  verbindlichkeit: string | null;
  rechtsgrundlage: string | null;
  naechsteFaellig: string | null;
  tageBisFaellig: number | null;
  ampel: Ampel;
}

export interface AnlageAnsicht {
  id: string;
  objekt: string;
  bezeichnung: string;
  gewerk: string;
  typ: string | null;
  hersteller: string | null;
  baujahr: number | null;
  standort: string | null;
  pflichten: PflichtAnsicht[];
}

/* ------------------------------------------------------------------ Live */

export interface ApiAnlage {
  id: string;
  objektId: string;
  gewerkBezeichnung?: string | null;
  gewerkCode: string;
  bezeichnung: string;
  typ: string | null;
  hersteller: string | null;
  baujahr: number | null;
  standort: string | null;
}
export interface ApiFaellig {
  id: string;
  anlageId: string;
  bezeichnung: string;
  verbindlichkeit: string | null;
  rechtsgrundlage: string | null;
  naechsteFaellig: string | null;
  tageBisFaellig: number | null;
  ampel: string;
}
export interface ApiObjektRef {
  id: string;
  bezeichnung: string;
}

export function baueAnlagenbuch(
  anlagen: ApiAnlage[],
  faellig: ApiFaellig[],
  objekte: ApiObjektRef[],
): AnlageAnsicht[] {
  const nameJeObjekt = new Map(objekte.map((o) => [o.id, o.bezeichnung]));
  const pflichtenJeAnlage = new Map<string, PflichtAnsicht[]>();
  for (const f of faellig) {
    const liste = pflichtenJeAnlage.get(f.anlageId) ?? [];
    liste.push({
      id: f.id,
      bezeichnung: f.bezeichnung,
      verbindlichkeit: f.verbindlichkeit,
      rechtsgrundlage: f.rechtsgrundlage,
      naechsteFaellig: f.naechsteFaellig,
      tageBisFaellig: f.tageBisFaellig,
      ampel: (f.ampel as Ampel) ?? 'gruen',
    });
    pflichtenJeAnlage.set(f.anlageId, liste);
  }
  return anlagen.map((a) => ({
    id: a.id,
    objekt: nameJeObjekt.get(a.objektId) ?? 'Objekt',
    bezeichnung: a.bezeichnung,
    gewerk: a.gewerkBezeichnung ?? a.gewerkCode,
    typ: a.typ,
    hersteller: a.hersteller,
    baujahr: a.baujahr,
    standort: a.standort,
    pflichten: pflichtenJeAnlage.get(a.id) ?? [],
  }));
}

/* ------------------------------------------------------------------ Demo */

export function demoAnlagenbuch(): AnlageAnsicht[] {
  return DEMO_ANLAGEN.map((a) => ({
    id: a.id,
    objekt: a.objekt,
    bezeichnung: a.bezeichnung,
    gewerk: a.gewerk,
    typ: a.typ,
    hersteller: a.hersteller,
    baujahr: a.baujahr,
    standort: a.standort,
    pflichten: DEMO_PFLICHTEN.filter((p) => p.anlage === a.bezeichnung).map((p) => ({
      id: p.id,
      bezeichnung: p.bezeichnung,
      verbindlichkeit: p.verbindlichkeit,
      rechtsgrundlage: p.rechtsgrundlage,
      naechsteFaellig: p.naechsteFaellig,
      tageBisFaellig: p.tageBisFaellig,
      ampel: p.ampel,
    })),
  }));
}
